import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { DISPOSITIONS, type DispositionChoice, PhoneNumber } from "@vd/ui";
import { useMemo } from "react";
import type { z } from "zod";
import { type CallQuery, callsInfinite, usersQuery } from "../../../api/admin";
import { allDispositionsQuery } from "../../../api/dispositions";
import type { AdminCallRow } from "../../../api/types";
import { CallViews } from "../../../components/CallViews";
import { DataTable } from "../../../components/DataTable";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Loading,
  PageHeader,
  Select,
} from "../../../components/ui";
import { callHistorySearch } from "../../../lib/callHistorySearch";
import { fmtDateTime, fmtDuration, fmtPhone } from "../../../lib/format";
import { dispositionName, routingBadge } from "../../../lib/labels";
import { useNavigate } from "../../../lib/navigate";

const searchSchema = callHistorySearch;

export const Route = createFileRoute("/admin/calls/")({
  staticData: { crumb: { label: "Calls", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  component: CallsPage,
});

const col = createColumnHelper<AdminCallRow>();

function CallsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const users = useQuery(usersQuery);
  const query: CallQuery = useMemo(
    () => ({
      user_id: search.user,
      from: search.from,
      to: search.to,
      direction: search.direction,
      outcome: search.disposition,
      dropped: search.dropped,
    }),
    [search],
  );
  const calls = useInfiniteQuery(callsInfinite(query));
  // The company's dispositions, archived ones too: the filter and the names (ADR 0015).
  const all = useQuery(allDispositionsQuery);
  const known: readonly DispositionChoice[] = all.data?.dispositions ?? DISPOSITIONS;
  const rows = useMemo(() => calls.data?.pages.flatMap((p) => p.items) ?? [], [calls.data]);
  const set = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const filtered = Object.values(search).some((v) => v !== undefined);

  const columns = useMemo(
    () => [
      col.accessor("started_at", {
        header: "Started",
        cell: (c) => <span className="tnum whitespace-nowrap">{fmtDateTime(c.getValue())}</span>,
      }),
      col.accessor((r) => r.user?.name ?? "", { id: "user", header: "User" }),
      col.accessor("direction", {
        header: "Direction",
        cell: (c) => (
          <span className="text-ink-2">{c.getValue() === "outbound" ? "Outbound" : "Inbound"}</span>
        ),
      }),
      col.accessor((r) => (r.direction === "outbound" ? r.to : r.from), {
        id: "number",
        header: "Number",
        cell: (c) => (
          <PhoneNumber
            e164={c.getValue()}
            types={c.row.original.phone_types}
            focusable={false}
            className="whitespace-nowrap"
          />
        ),
      }),
      col.accessor("contact_name", {
        header: "Contact",
        cell: (c) => c.getValue() ?? <span className="text-ink-3">Unknown</span>,
      }),
      col.accessor("duration_s", {
        header: "Duration",
        meta: { align: "right" },
        cell: (c) => fmtDuration(c.getValue()),
      }),
      col.accessor("disposition", {
        header: "Disposition",
        cell: (c) => {
          const r = c.row.original;
          return (
            <span className="flex items-center gap-1.5">
              {r.state !== "ended" ? (
                <Badge tone="accent">In progress</Badge>
              ) : c.getValue() ? (
                dispositionName(c.getValue(), known, r.disposition_label)
              ) : (
                ""
              )}
              {r.dropped ? <Badge tone="danger">Dropped</Badge> : null}
            </span>
          );
        },
      }),
      col.accessor("routing_status", {
        header: "CRM",
        cell: (c) => {
          const b = routingBadge(c.getValue());
          return <Badge tone={b.tone}>{b.label}</Badge>;
        },
      }),
    ],
    [known],
  );

  return (
    <>
      <PageHeader title="Calls" />
      <CallViews search={search} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Select
          aria-label="User"
          className="w-44"
          value={search.user ?? ""}
          onValueChange={(v) => set({ user: v || undefined })}
          options={[
            { value: "", label: "Everyone" },
            ...(users.data ?? []).map((u) => ({ value: u.id, label: u.name })),
          ]}
        />
        <Input
          type="date"
          aria-label="From date"
          className="w-40"
          value={search.from ?? ""}
          max={search.to}
          onChange={(e) => set({ from: e.target.value || undefined })}
        />
        <Input
          type="date"
          aria-label="To date"
          className="w-40"
          value={search.to ?? ""}
          min={search.from}
          onChange={(e) => set({ to: e.target.value || undefined })}
        />
        <Select
          aria-label="Direction"
          className="w-36"
          value={search.direction ?? ""}
          onValueChange={(v) =>
            set({
              direction: (v || undefined) as "inbound" | "outbound" | undefined,
            })
          }
          options={[
            { value: "", label: "Both directions" },
            { value: "outbound", label: "Outbound" },
            { value: "inbound", label: "Inbound" },
          ]}
        />
        <Select
          aria-label="Disposition"
          className="w-44"
          value={search.disposition ?? ""}
          onValueChange={(v) => set({ disposition: v || undefined })}
          options={[
            { value: "", label: "Any disposition" },
            ...known.map((d) => ({
              value: d.id,
              label: d.archived ? `${d.label} (archived)` : d.label,
            })),
          ]}
        />
        <Button
          variant={search.dropped ? "danger" : "secondary"}
          aria-pressed={Boolean(search.dropped)}
          onClick={() => set({ dropped: search.dropped ? undefined : true })}
        >
          Dropped only
        </Button>
        {filtered ? (
          <Button variant="ghost" onClick={() => navigate({ search: {}, replace: true })}>
            Clear
          </Button>
        ) : null}
      </div>
      <Card>
        {calls.isPending ? (
          <Loading />
        ) : calls.isError ? (
          <ErrorState error={calls.error} onRetry={() => calls.refetch()} />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(r) => r.id}
              onRowClick={(r) => navigate({ to: "/admin/calls/$callId", params: { callId: r.id } })}
              rowLabel={(r) =>
                `Call with ${r.contact_name ?? fmtPhone(r.direction === "outbound" ? r.to : r.from)}, ${fmtDateTime(r.started_at)}`
              }
              rowClassName={(r) => (r.dropped ? "shadow-[inset_3px_0_0_var(--vd-danger)]" : undefined)}
              empty={
                <EmptyState
                  title={filtered ? "No calls match these filters" : "No calls yet"}
                  description={
                    filtered
                      ? "Try a wider date range or clear the filters."
                      : "Calls show up here as soon as reps start dialing."
                  }
                />
              }
            />
            {calls.hasNextPage ? (
              <div className="flex justify-center border-t border-hairline p-3">
                <Button size="sm" onClick={() => calls.fetchNextPage()} busy={calls.isFetchingNextPage}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </>
  );
}
